"use client"

import Image from "next/image"
import Link from "next/link"
import React, { useState } from "react"
import "./ArticleBlurb.css"
import Icon from "../Icon"

export type Author = {
  name: string
  title?: string
  company?: string
  avatar?: {
    url?: string
  }
  teaserBio?: string
  fullBio?: string
  readMoreUrl?: string
  slug?: string
}

export type ArticleBlurbProps = {
  authors: Author[]
}

const MAX_BIO_LENGTH = 300

const ArticleBlurb: React.FC<ArticleBlurbProps> = ({ authors }) => {
  return (
    <div className="bg-[#f5f5f5] py-12 md:py-16 lg:py-24 w-full flex">
      <div className="max-w-7xl article-content-block w-full mx-auto">
        <div className="article-blurb">
          {authors.map((author, index) => (
            <AuthorCard key={index} author={author} />
          ))}
        </div>
      </div>
    </div>
  )
}

type AuthorCardProps = {
  author: Author
}

const AuthorCard: React.FC<AuthorCardProps> = ({ author }) => {
  const [expanded, setExpanded] = useState(false)

  const shouldTruncate =
    author.teaserBio && author.teaserBio.length > MAX_BIO_LENGTH
  const displayedBio =
    shouldTruncate && !expanded
      ? author.teaserBio?.slice(0, MAX_BIO_LENGTH) + "..."
      : author.teaserBio

  const authorPageUrl = author.slug ? `/authors/${author.slug}` : "#"

  return (
    <div className="article-blurb__author">
      <div className="article-blurb__author-header">
        {author?.avatar?.url ? (
          <div className="article-blurb__author-image-wrapper">
            <Image
              src={author.avatar.url}
              alt={author.name || "Author image"}
              width={86}
              height={86}
              className="article-blurb__author-image"
            />
          </div>
        ) : (
          <div className="article-blurb__author-image-placeholder" />
        )}
        <div className="article-blurb__author-info">
          <h3 className="article-blurb__author-name text-mxpGray10">
            {author.name}
          </h3>
          <div className="article-blurb__author-credentials flex items-center gap-1">
            {author.title && (
              <p className="article-blurb__author-title text-mxpGray10">
                {author.title},{" "}
              </p>
            )}
            {author.company && (
              <p className="article-blurb__author-company text-mxpGray10">
                {author.company}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="article-blurb__author-bio-section">
        {author.teaserBio && (
          <p className="article-blurb__author-bio text-mxpGray10">
            {displayedBio}
            {shouldTruncate && (
              <button
                onClick={() => setExpanded(true)}
                className="article-blurb__read-more-btn text-mxpTrimbleBlue"
              >
                {!expanded ? " Read more" : ""}
              </button>
            )}
          </p>
        )}

        <div className="article-blurb__author-links">
          {author.readMoreUrl && (
            <Link
              href={author.readMoreUrl}
              className="article-blurb__author-link text-mxpTrimbleBlue"
            >
              Read More
            </Link>
          )}

          {author.slug && (
            <Link
              href={authorPageUrl}
              className="article-blurb__author-link flex items-center mt-6 text-mxpTrimbleBlue"
            >
              More by {author.name}
              <Icon
                label="View Post"
                type={"t2u-chevron-right"}
                size={20}
                className="ml-2"
              />
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

export default ArticleBlurb
